import { useQuery } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useState } from "react";
import { fetchSearchProductData } from "../api/searchProduct.api";
import { Family } from "../api/models/fullProductData.models";
import { fetchCompanyFullProductData } from "../api/fullProductData.api";
import { fetchCompanyFullModelData } from "../api/fullModelData.api";

interface UseProductProps {
  companyId?: number;
}

const useSearchProductsWithEan = ({ companyId }: UseProductProps) => {
  const [ean, setEan] = useState<string>("");
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  const {
    data: productData,
    error: productError,
    isLoading: isLoadingProduct,
  } = useQuery({
    queryKey: ["searchProducts", ean],
    queryFn: () =>
      fetchSearchProductData(
        bearerToken as string,
        companyId as number,
        true,
        undefined,
        [ean],
        undefined,
        true,
      ),
    enabled: !!ean && ean !== "",
  });

  const {
    data: fullProductData,
    error: fullProductError,
    isLoading: isLoadingFullProduct,
  } = useQuery({
    queryKey: ["fullProductData", ean],
    queryFn: () =>
      fetchCompanyFullProductData(
        bearerToken as string,
        companyId as number,
        [ean],
        true,
      ),
    enabled:
      !!productData?.data?.length &&
      !!ean &&
      productData?.data[0].family === Family.product,
  });

  // FullModel fetch if no FullProduct found
  const {
    data: fullModelData,
    error: fullModelError,
    isLoading: isLoadingFullModel,
  } = useQuery({
    queryKey: ["fullModelData", ean],
    queryFn: () => {
      return fetchCompanyFullModelData(
        bearerToken as string,
        companyId as number,
        [ean],
        false,
      );
    },
    enabled:
      !!productData?.data?.length &&
      !!ean &&
      productData?.data[0].family === Family.product_model,
  });

  // Prefetching next pages if needed can be added here
  const eanSearchData = fullProductData || fullModelData;
  // Covers both the initial product search and the follow-up full data fetch
  const eanSearchLoading =
    isLoadingProduct || isLoadingFullProduct || isLoadingFullModel;
  const eanSearchError = fullProductError || fullModelError;

  return {
    productData,
    productError,
    eanSearchData,
    eanSearchError,
    eanSearchLoading,
    searchEan: ean,
    setSearchEan: setEan,
    isLoadingProduct,
  };
};

export default useSearchProductsWithEan;
